import { ReactNode, forwardRef, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { cn } from '../../utils/cn';
import { Text } from '../Text';

export interface InputProps {
  size?: 'sm' | 'md';
  /** Left side decorator/icon */
  leftDecorator?: ReactNode;
  /** Right side decorator/icon */
  rightDecorator?: ReactNode;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the input has an error state */
  isError?: boolean;
  /** Label text displayed above the input */
  label?: string | ReactNode;
  /** Hint text displayed below the input */
  hintText?: string;
  /** Current input value */
  value?: string;
  /** Change handler that will be debounced */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Input type */
  type?: 'text' | 'email' | 'password' | 'number';
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Additional CSS classes */
  className?: string;
  /** Input ID for accessibility */
  id?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Auto focus on mount */
  autoFocus?: boolean;
}

export const Input = forwardRef<
  HTMLInputElement,
  InputProps &
    Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof InputProps>
>(
  (
    {
      size = 'md',
      leftDecorator,
      rightDecorator,
      disabled = false,
      isError = false,
      label,
      hintText,
      value,
      onChange,
      placeholder,
      type = 'text',
      debounceMs = 0,
      className,
      id,
      required = false,
      autoFocus = false,
      ...rest
    },
    ref
  ) => {
    const shouldDebounce = debounceMs > 0;
    const [internalValue, setInternalValue] = useState(value);
    const debouncedOnChange = useDebouncedCallback(onChange, debounceMs);

    // Update internal value when external value changes
    useEffect(() => {
      if (shouldDebounce) {
        setInternalValue(value);
      }
    }, [value, shouldDebounce]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (shouldDebounce) {
        setInternalValue(newValue);
        debouncedOnChange(newValue);
      } else {
        onChange(newValue);
      }
    };

    // Styled container that holds the input and decorators
    const inputContainerClasses = cn(
      // Base container styles
      'flex w-full items-center gap-2 rounded-md border bg-transparent  transition-colors',
      {
        'px-2 py-1': size === 'sm',
        'px-3 py-2': size === 'md',
      },
      'focus-within:outline-none',
      // Default state
      'border-secondary focus-within:border-brand',
      // Error state
      isError && 'border-error focus-within:border-error',
      // Disabled state
      disabled && 'cursor-not-allowed bg-secondary opacity-50'
    );

    // Unstyled input that fills the container
    const inputClasses = cn(
      'max-w-full flex-1 border-none bg-transparent p-0 outline-none',
      {
        'text-xs': size === 'sm',
        'text-sm': size === 'md',
      },
      'placeholder:text-tertiary',
      disabled && 'cursor-not-allowed'
    );

    // Decorator styles
    const decoratorClasses = 'flex items-center text-tertiary';
    const leftDecoratorClasses = cn(decoratorClasses);
    const rightDecoratorClasses = cn(decoratorClasses);

    const labelClasses = cn(
      'mb-1 block text-sm font-medium text-primary',
      disabled && 'text-disabled'
    );

    const hintTextClasses = cn(
      'mt-1 text-xs',
      isError ? 'text-error' : 'text-tertiary'
    );

    const containerClasses = cn('w-full', className);

    return (
      <div className={containerClasses}>
        {label && (
          <Text as="label" className={labelClasses} variant="xs">
            {label}
            {required && <span className="ml-1 text-error">*</span>}
          </Text>
        )}

        <div className={inputContainerClasses}>
          {leftDecorator && (
            <div className={leftDecoratorClasses}>{leftDecorator}</div>
          )}

          <input
            ref={ref}
            id={id}
            type={type}
            value={shouldDebounce ? internalValue : value}
            onChange={handleInputChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            className={inputClasses}
            {...rest}
          />

          {rightDecorator && (
            <div className={rightDecoratorClasses}>{rightDecorator}</div>
          )}
        </div>

        {hintText && (
          <Text className={hintTextClasses} variant="xs" weight="normal">
            {hintText}
          </Text>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
